import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { adminStore, type LessonPlan } from '../../store/adminStore';

const guides = [
  {
    icon: 'ri-game-fill',
    color: 'bg-primary-orange/10',
    iconColor: 'text-primary-orange',
    title: 'Hướng dẫn sử dụng Túi Trò Chơi',
    steps: [
      'Vào thẻ "Túi trò chơi" để xem danh sách trò chơi dân gian Bahnar',
      'Nhấn vào từng trò chơi để xem Giới thiệu, Luật chơi, Cách chơi và Video minh họa',
      'Tổ chức cho học sinh chơi theo hướng dẫn ngay tại lớp hoặc sân trường',
      'Khuyến khích học sinh đăng nhập để lưu tiến trình khám phá',
    ],
  },
  {
    icon: 'ri-seedling-fill',
    color: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'Khai thác Hạt Giống Tri Thức',
    steps: [
      'Truy cập thẻ "Hạt giống tri thức" để xem video và bài học về văn hóa Bahnar',
      'Chọn chủ đề phù hợp với bài học, sử dụng video như tư liệu minh họa',
      'Tải tài liệu học liệu đính kèm (nếu có) để phân phát cho học sinh',
      'Sử dụng phần "Hộp thư" để đóng góp tư liệu mới về văn hóa Bahnar',
    ],
  },
  {
    icon: 'ri-community-fill',
    color: 'bg-primary-yellow/20',
    iconColor: 'text-primary-yellow',
    title: 'Tận dụng trang Cộng Đồng',
    steps: [
      'Đăng ảnh và câu chuyện từ buổi học vào trang Cộng đồng',
      'Khuyến khích học sinh và phụ huynh đăng chia sẻ trải nghiệm',
      'Lọc bài đăng theo vai trò để theo dõi bài của giáo viên đồng nghiệp',
      'Tương tác like và bình luận để tạo không khí cộng đồng sôi nổi',
    ],
  },
];

export default function TeacherPage() {
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDownload, setShowDownload] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>(() => adminStore.getStore().lessonPlans);
  const [activeGuide, setActiveGuide] = useState<number | null>(null);

  useEffect(() => {
    const auth = localStorage.getItem('teacher_authenticated') === 'true';
    if (!auth) {
      navigate('/community');
    } else {
      setIsAuth(true);
    }
  }, [navigate]);

  // Đồng bộ với adminStore khi admin thay đổi KHBD
  useEffect(() => {
    const handler = () => {
      setLessonPlans(adminStore.getStore().lessonPlans);
    };
    window.addEventListener('bahnar-admin-updated', handler);
    return () => window.removeEventListener('bahnar-admin-updated', handler);
  }, []);

  const filteredPlans = lessonPlans.filter(p =>
    searchQuery.trim() === '' ||
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.grade.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleAddShared = (plan: Omit<LessonPlan, 'id'>) => {
    const newPlan: LessonPlan = { ...plan, id: `plan-${Date.now()}` };
    const updated = [...lessonPlans, newPlan];
    adminStore.updateLessonPlans(updated);
    setLessonPlans(updated);
    setShowShareModal(false);
  };

  if (!isAuth) return null;

  return (
    <div className="min-h-screen bg-primary-cream">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-14 relative overflow-hidden">
        {/* Books background image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/1cc4dff2fab325f8b161c01cf2cdaccb/3bc0f45f94fc6717507c9a76a0ff9950.png"
            alt=""
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-beige/90 to-primary-cream/98"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-orange/10 rounded-full mb-4">
                <i className="ri-graduation-cap-fill text-primary-orange"></i>
                <span className="text-sm font-bold text-primary-orange uppercase tracking-wider">Khu vực Giáo viên</span>
              </div>
              <h1 className="text-4xl font-extrabold text-primary-brown-dark mb-3">
                Tài nguyên dành cho Giáo viên
              </h1>
              <p className="text-base text-primary-brown-light max-w-xl">
                Mẫu kế hoạch bài dạy, tài nguyên giảng dạy và hướng dẫn khai thác website về văn hóa Bahnar.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 space-y-10">

          {/* Search */}
          <div className="flex items-center gap-3 bg-white rounded-2xl p-4">
            <div className="relative flex-1">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-primary-brown-light text-lg"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Tìm kiếm theo tên, môn học, khối lớp, từ khóa..."
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
              />
            </div>
            <button className="flex items-center gap-2 px-5 py-3 bg-primary-orange text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line"></i>
              </div>
              Tìm kiếm
            </button>
          </div>

          {/* Lesson Plans */}
          <div>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h2 className="text-2xl font-extrabold text-primary-brown-dark flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-primary-orange/10 rounded-xl">
                  <i className="ri-file-text-fill text-primary-orange"></i>
                </div>
                Kế Hoạch Bài Dạy (KHBD)
              </h2>
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-2-line"></i>
                Đăng KHBD của tôi
              </button>
            </div>

            {filteredPlans.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 flex items-center justify-center bg-primary-orange/10 rounded-full mx-auto mb-4">
                  <i className="ri-file-text-line text-primary-orange text-3xl"></i>
                </div>
                <h3 className="font-bold text-primary-brown-dark mb-2">Chưa có KHBD nào</h3>
                <p className="text-sm text-primary-brown-light mb-4">Hãy là người đầu tiên chia sẻ kế hoạch bài dạy!</p>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-upload-2-line"></i>
                  Đăng KHBD của tôi
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {filteredPlans.map(plan => (
                  <SharedPlanCard key={plan.id} plan={plan} />
                ))}
              </div>
            )}
          </div>

          {/* How to use guides */}
          <div>
            <h2 className="text-2xl font-extrabold text-primary-brown-dark mb-5 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-primary-yellow/20 rounded-xl">
                <i className="ri-book-open-fill text-primary-yellow"></i>
              </div>
              Cách sử dụng &amp; Khai thác website hiệu quả
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {guides.map((g, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setActiveGuide(activeGuide === i ? null : i)}
                    className="w-full p-5 flex items-center gap-4 cursor-pointer text-left"
                  >
                    <div className={`w-12 h-12 flex items-center justify-center ${g.color} rounded-2xl flex-shrink-0`}>
                      <i className={`${g.icon} ${g.iconColor} text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-primary-brown-dark text-sm leading-snug">{g.title}</h3>
                      <p className="text-xs text-primary-brown-light mt-0.5">Nhấn để xem hướng dẫn</p>
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center text-primary-brown-light">
                      <i className={`${activeGuide === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`}></i>
                    </div>
                  </button>
                  {activeGuide === i && (
                    <div className="px-5 pb-5 border-t border-primary-cream">
                      <ol className="mt-4 space-y-2.5">
                        {g.steps.map((step, si) => (
                          <li key={si} className="flex items-start gap-3 text-sm text-primary-brown-light">
                            <span className="min-w-5 h-5 flex items-center justify-center bg-primary-orange/15 text-primary-orange font-bold text-xs rounded-full flex-shrink-0 mt-0.5">
                              {si + 1}
                            </span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <Footer />

      {showDownload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-fill text-3xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-bold text-primary-brown-dark mb-2">Đã chuẩn bị tải!</h3>
            <p className="text-primary-brown-light text-sm mb-6">Tính năng tải file KHBD sẽ sớm được cập nhật. Thầy/Cô vui lòng liên hệ ban quản lý để nhận file.</p>
            <button onClick={() => setShowDownload(null)} className="px-8 py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl cursor-pointer whitespace-nowrap">
              Đã hiểu
            </button>
          </div>
        </div>
      )}

      {showShareModal && (
        <SharePlanModal
          onClose={() => setShowShareModal(false)}
          onSubmit={handleAddShared}
        />
      )}
    </div>
  );
}

interface SharePlanModalProps {
  onClose: () => void;
  onSubmit: (plan: Omit<LessonPlan, 'id'>) => void;
}

function SharedPlanCard({ plan }: { plan: LessonPlan }) {

  return (
    <article className="bg-white rounded-3xl p-6">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 flex-shrink-0 rounded-full bg-primary-orange/20 flex items-center justify-center">
          <i className="ri-graduation-cap-fill text-xl text-primary-orange"></i>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-primary-brown-dark text-base">{plan.author}</span>
            <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-primary-orange/15 text-primary-orange">Giáo viên</span>
            {plan.grade && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-primary-yellow/20 text-primary-brown font-medium">{plan.grade}</span>
            )}
          </div>
          <p className="text-sm text-primary-brown-light mt-0.5">{plan.duration} · {plan.subject}</p>
        </div>
      </div>

      <h3 className="text-lg font-extrabold text-primary-brown-dark mb-2">{plan.title}</h3>
      {plan.description && (
        <p className="text-primary-brown-light leading-relaxed mb-3 text-sm">{plan.description}</p>
      )}

      {plan.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {plan.tags.map(tag => (
            <span key={tag} className="text-xs px-3 py-1 bg-primary-cream text-primary-brown-light rounded-full">#{tag}</span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-4 pt-4 border-t border-primary-brown-light/10">
        <button className="flex items-center gap-2 text-sm font-medium text-primary-brown-light hover:text-red-500 transition-colors cursor-pointer">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-heart-line"></i></div>
          Hay lắm!
        </button>
        <button className="flex items-center gap-2 text-sm font-medium text-primary-brown-light hover:text-primary-orange transition-colors cursor-pointer">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-chat-1-line"></i></div>
          Bình luận
        </button>
        <button className="flex items-center gap-2 text-sm font-medium text-primary-brown-light hover:text-primary-orange transition-colors cursor-pointer ml-auto">
          <div className="w-5 h-5 flex items-center justify-center"><i className="ri-share-line"></i></div>
          Chia sẻ
        </button>
      </div>
    </article>
  );
}

function SharePlanModal({ onClose, onSubmit }: SharePlanModalProps) {
  const [form, setForm] = useState({
    title: '',
    subject: '',
    grade: '',
    duration: '45 phút',
    author: '',
    description: '',
    tags: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!form.title.trim() || !form.author.trim()) return;
    onSubmit({
      title: form.title,
      subject: form.subject,
      grade: form.grade,
      duration: form.duration,
      author: form.author,
      description: form.description,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    });
    setSubmitted(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-fill text-3xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-bold text-primary-brown-dark mb-2">Đã đăng thành công!</h3>
            <p className="text-primary-brown-light text-sm">Cảm ơn Thầy/Cô đã chia sẻ tài nguyên!</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-orange/10 rounded-2xl">
                  <i className="ri-upload-2-line text-primary-orange text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-primary-brown-dark">Chia sẻ Kế hoạch Bài dạy</h3>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-primary-cream cursor-pointer">
                <i className="ri-close-line text-primary-brown-light"></i>
              </button>
            </div>

            <div className="space-y-4 mb-5">
              <div>
                <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Họ tên Giáo viên <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.author}
                  onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                  placeholder="VD: Cô Nguyễn Thị Hoa"
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Môn học</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="VD: Thể dục, Âm nhạc..."
                    className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Khối lớp</label>
                  <input
                    type="text"
                    value={form.grade}
                    onChange={e => setForm(f => ({ ...f, grade: e.target.value }))}
                    placeholder="VD: Lớp 3, Lớp 4–5..."
                    className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Thời lượng</label>
                <input
                  type="text"
                  value={form.duration}
                  onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                  placeholder="VD: 45 phút, 90 phút..."
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Tiêu đề bài dạy <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="VD: Trò chơi Kéo co – Rèn tinh thần đồng đội"
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Mô tả ngắn</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value.slice(0, 300) }))}
                  placeholder="Mô tả mục tiêu và nội dung chính của bài dạy..."
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors resize-none h-20"
                />
                <p className="text-xs text-primary-brown-light mt-1">{form.description.length}/300</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-primary-brown-dark mb-1.5">Từ khóa (phân cách bằng dấu phẩy)</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="VD: trò chơi, dân gian, đồng đội"
                  className="w-full px-4 py-3 rounded-xl border border-primary-brown-light/20 bg-primary-cream text-sm text-primary-brown-dark placeholder-primary-brown-light focus:outline-none focus:border-primary-orange transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 border-2 border-primary-brown-light/20 rounded-xl text-primary-brown-light font-semibold cursor-pointer whitespace-nowrap">
                Hủy
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.title.trim() || !form.author.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Đăng chia sẻ
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
